import { useEffect, useMemo, useState, type ReactNode, type CSSProperties } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCircle, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useTheme, type ThemeTokens } from '@course-authoring/shared-types';
import { saveFeedbackColors } from '../theme/saveFeedbackColors';

const SAVED_CONFIRMATION_MS = 3000;
const SAVE_BUTTON_WIDTH = 132;

export interface PageSaveHeaderProps {
  title: string;
  isDirty: boolean;
  isSaving: boolean;
  saved: boolean;
  disabled?: boolean;
  error?: string | null;
  progressLabel?: string;
  preparationStatus?: ReactNode;
  savingStatusProvided?: boolean;
  onSave: () => void;
}

/** Opt-in save presentation. Persistence and draft ownership stay with the page. */
export function PageSaveHeader({ title, isDirty, isSaving, saved, disabled = false,
  error, progressLabel = 'Saving…', preparationStatus, savingStatusProvided = false, onSave }: PageSaveHeaderProps) {
  const { tokens } = useTheme();
  const styles = useMemo(() => createStyles(tokens), [tokens]);
  const [showSaved, setShowSaved] = useState(false);
  const [saveHovered, setSaveHovered] = useState(false);
  useEffect(() => {
    if (!saved || isDirty || isSaving) {
      setShowSaved(false);
      return;
    }
    setShowSaved(true);
    const timeout = window.setTimeout(() => setShowSaved(false), SAVED_CONFIRMATION_MS);
    return () => window.clearTimeout(timeout);
  }, [saved, isDirty, isSaving]);
  const saveDisabled = disabled || !isDirty || isSaving;

  return (
    <header style={styles.header}>
      <div style={styles.row}>
        <h1 style={styles.title}>{title}</h1>
        <div style={styles.actions}>
          <span role="status" aria-live="polite" style={{ ...styles.status,
            color: showSaved ? saveFeedbackColors.savedForeground : tokens.text.muted }}>
            {isSaving ? '' : isDirty ? <span style={styles.dirtyPill}>
              <span aria-hidden="true" style={styles.dirtyDot}>
                <FontAwesomeIcon icon={faCircle} style={styles.dotIcon} />
              </span>
              Unsaved changes
            </span> : showSaved ? <span style={styles.savedLabel}>
              <FontAwesomeIcon icon={faCheck} aria-hidden="true" style={styles.statusIcon} /> Saved
            </span> : ''}
          </span>
          {preparationStatus}
          <button type="button" disabled={saveDisabled} onClick={onSave} aria-busy={isSaving}
            onMouseEnter={() => setSaveHovered(true)} onMouseLeave={() => setSaveHovered(false)}
            style={{ ...styles.saveButton, cursor: saveDisabled ? 'default' : 'pointer',
              background: saveDisabled ? saveFeedbackColors.disabledBackground : saveHovered ? saveFeedbackColors.actionHoverBackground : saveFeedbackColors.actionBackground,
              color: saveDisabled ? saveFeedbackColors.disabledForeground : saveFeedbackColors.actionForeground }}>
            {isSaving && !savingStatusProvided && <FontAwesomeIcon icon={faSpinner} spin aria-hidden="true" style={styles.statusIcon} />}
            {isSaving && !savingStatusProvided ? progressLabel : 'Save'}
          </button>
        </div>
      </div>
      {error && <p role="alert" style={styles.error}>{error}</p>}
    </header>
  );
}

function createStyles(tokens: ThemeTokens) {
  return {
    header: {
      position: 'sticky', top: 0, zIndex: 2, flexShrink: 0,
      background: tokens.backgrounds.page, borderBottom: `1px solid ${tokens.borders.default}`,
      padding: `${tokens.spacing.md} ${tokens.spacing.xl}`, color: tokens.text.primary,
    },
    row: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: tokens.spacing.md, flexWrap: 'wrap',
    },
    title: {
      margin: 0, fontSize: tokens.typography.sizes.md,
      fontWeight: tokens.typography.h3.weight, lineHeight: tokens.typography.lineHeights.uiSoft,
    },
    actions: {
      display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
      gap: tokens.spacing.md, marginLeft: 'auto', flexWrap: 'wrap',
    },
    status: { minWidth: '8em', textAlign: 'right', fontSize: tokens.typography.sizes.sm },
    dirtyPill: {
      display: 'inline-flex', alignItems: 'center', gap: 7,
      padding: '3px 11px', borderRadius: 14, lineHeight: 1.5,
      background: saveFeedbackColors.dirtyBackground,
      border: `1px solid ${saveFeedbackColors.dirtyBorder}`,
      color: saveFeedbackColors.dirtyForeground,
    },
    dirtyDot: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 15, height: 15 },
    dotIcon: { fontSize: 8 },
    savedLabel: { display: 'inline-flex', alignItems: 'center', gap: 7 },
    statusIcon: { fontSize: 15 },
    saveButton: {
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: tokens.spacing.sm,
      width: SAVE_BUTTON_WIDTH, minHeight: 35, flexShrink: 0, padding: '7px 20px',
      border: 'none', borderRadius: 5, font: 'inherit', lineHeight: 1.5, fontSize: tokens.typography.sizes.sm,
      fontWeight: 600, whiteSpace: 'nowrap',
    },
    error: { margin: `${tokens.spacing.sm} 0 0`, color: tokens.ui.errorText, fontSize: tokens.typography.sizes.sm },
  } satisfies Record<string, CSSProperties>;
}
